import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { productsData } from '../productsData';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import axios from 'axios';
import { toast } from '../hooks/use-toast';
import { ArrowRight, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductsPage = () => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleEnquiry = (product) => {
    setSelectedProduct(product);
    setIsDialogOpen(true);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/enquiry`, {
        ...formData,
        product: selectedProduct.name
      });

      toast({
        title: "Enquiry Submitted!",
        description: "We'll get back to you soon.",
      });

      setFormData({ name: '', email: '', mobile: '', message: '' });
      setIsDialogOpen(false);
    } catch (error) {
      console.error('Error submitting enquiry:', error);
      toast({
        title: "Error",
        description: "Failed to submit enquiry. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>IT Products - R-Technos | Hardware, Software & Security Solutions</title>
        <meta name="description" content="Browse our comprehensive range of IT products including hardware, software, cloud services, and security solutions from leading brands." />
        <meta name="keywords" content="IT products Hyderabad, hardware, software, cloud services, security solutions, business IT" />
        <link rel="canonical" href="https://rtechnos.com/products" />
        <meta property="og:title" content="IT Products - R-Technos" />
        <meta property="og:description" content="Quality IT products and solutions for your business needs." />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="min-h-screen bg-white pt-20" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, SF Pro Text, sans-serif' }}>
        {/* Hero Section */}
        <section className="bg-gradient-to-b from-white via-gray-50 to-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-blue-600 text-sm font-semibold tracking-wider uppercase mb-3">
              Our Products
            </p>
            <h1 className="text-5xl md:text-6xl font-semibold text-gray-900 mb-6" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, SF Pro Display, sans-serif', letterSpacing: '-0.02em' }}>
              Premium IT Products
              <br />
              <span className="bg-gradient-to-r from-blue-600 to-cyan-500 text-transparent bg-clip-text">
                for Modern Business
              </span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive range of IT products from leading brands. Quality hardware, software, and security solutions tailored for your business needs.
            </p>
          </div>
        </section>

        {/* Products by Category */}
        {productsData.map((category) => (
          <section key={category.id} className="py-16 odd:bg-white even:bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center mb-10">
                <div className="w-1 h-12 bg-blue-600 rounded mr-4"></div>
                <h2 className="text-4xl font-semibold text-gray-900" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, SF Pro Display, sans-serif', letterSpacing: '-0.02em' }}>
                  {category.category}
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {category.items.map((product) => (
                  <div
                    key={product.id}
                    className="group bg-white border border-gray-200 rounded-3xl overflow-hidden hover:shadow-xl hover:border-blue-200 transition-all duration-300"
                  >
                    <div className="aspect-video overflow-hidden bg-gray-100">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-semibold text-gray-900 mb-2" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, SF Pro Display, sans-serif' }}>
                        {product.name}
                      </h3>
                      <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                        {product.description}
                      </p>

                      {/* Features */}
                      <ul className="space-y-2 mb-4">
                        {product.features.slice(0, 3).map((feature, index) => (
                          <li key={index} className="text-gray-600 text-xs flex items-start">
                            <CheckCircle className="text-blue-600 mr-2 flex-shrink-0 mt-0.5" size={14} />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>

                      <Dialog open={isDialogOpen && selectedProduct?.id === product.id} onOpenChange={setIsDialogOpen}>
                        <DialogTrigger asChild>
                          <Button
                            onClick={() => handleEnquiry(product)}
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full"
                          >
                            Enquire Now
                            <ArrowRight className="ml-2" size={16} />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-white border-gray-200">
                          <DialogHeader>
                            <DialogTitle className="text-gray-900 text-2xl" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, SF Pro Display, sans-serif' }}>
                              Product Enquiry
                            </DialogTitle>
                            <p className="text-gray-600 text-sm mt-2">{selectedProduct?.name}</p>
                          </DialogHeader>
                          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                            <div>
                              <label className="text-gray-700 text-sm font-medium mb-2 block">
                                Your Name *
                              </label>
                              <Input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                className="border-gray-300 focus:border-blue-600 rounded-xl"
                                placeholder="John Doe"
                              />
                            </div>
                            <div>
                              <label className="text-gray-700 text-sm font-medium mb-2 block">
                                Email *
                              </label>
                              <Input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                className="border-gray-300 focus:border-blue-600 rounded-xl"
                                placeholder="john@example.com"
                              />
                            </div>
                            <div>
                              <label className="text-gray-700 text-sm font-medium mb-2 block">
                                Mobile *
                              </label>
                              <Input
                                type="tel"
                                name="mobile"
                                value={formData.mobile}
                                onChange={handleChange}
                                required
                                className="border-gray-300 focus:border-blue-600 rounded-xl"
                                placeholder="+91 98765 43210"
                              />
                            </div>
                            <div>
                              <label className="text-gray-700 text-sm font-medium mb-2 block">
                                Message
                              </label>
                              <Textarea
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                                rows={3}
                                className="border-gray-300 focus:border-blue-600 rounded-xl"
                                placeholder="Tell us about your requirements..."
                              />
                            </div>
                            <Button
                              type="submit"
                              disabled={isSubmitting}
                              className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full py-6"
                            >
                              {isSubmitting ? 'Submitting...' : 'Submit Enquiry'}
                            </Button>
                          </form>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        ))}

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-blue-50 to-cyan-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl md:text-5xl font-semibold text-gray-900 mb-6" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, SF Pro Display, sans-serif', letterSpacing: '-0.02em' }}>
              Can't Find What You're Looking For?
            </h2>
            <p className="text-gray-600 text-lg mb-8">
              Our team can source any IT product you need. Contact us with your requirements and we'll get you the best solution.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/contact">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-base rounded-full shadow-md hover:shadow-lg transition-all">
                  Contact Us
                </Button>
              </a>
              <a href="tel:+917989109423">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-blue-600 text-blue-600 hover:bg-white px-8 py-6 text-base rounded-full transition-all"
                >
                  Call: +91 79891 09423
                </Button>
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ProductsPage;
